<template>
  <div class="table-container">
    <div class="header">
      <div class="title">{{ $t('usersAccount') }} ({{ info.users.length }})</div>
    </div>

    <div class="control-container">
      <div class="form-container search">
        <input v-model="searchValue" type="text" class="input search" :placeholder="$t('usersSearch')" />
      </div>

      <RouterLink class="main-button" to="/settings/users/add">
        {{ $t('addAUser') }}
      </RouterLink>
    </div>

    <table class="table hide-medium" border="0" cellspacing="0" cellpadding="0">
      <thead class="table-header">
        <tr>
          <th v-for="data in headers" :key="data.id" class="table-header-item">{{ data }}</th>
        </tr>
      </thead>

      <tbody v-if="!filteredUser.length" class="table-content">
        <tr class="table-line">
          <td class="table-item no-result">
            {{ $t('noResults') }}
          </td>
        </tr>
      </tbody>

      <tbody v-else class="table-content">
        <tr v-for="user in filteredUser" :key="user.id" class="table-line">
          <td class="table-item">
            <div class="item bold">{{ user.firstName }} {{ user.lastName }}</div>
          </td>

          <td class="table-item">
            <div class="item">{{ user.email }}</div>
          </td>

          <td class="table-item">
            <div class="item">{{ user.isMainUser ? $t('mainUser') : $t('secondaryUser') }}</div>
          </td>

          <td class="table-item">
            <div class="item">{{ $utils.formatDate(user.createdAt) }}</div>
          </td>

          <td class="table-item">
            <div class="item">{{ !user.isActivated ? $utils.formatDate(user.updatedAt) : '-' }}</div>
          </td>

          <td class="table-item">
            <div class="item">
              <span
                class="link-button"
                @click="$router.push(`/settings/users/edit?email=${user.email}&firstName=${user.firstName}&lastName=${user.lastName}&userId=${user.id}`)"
                >{{ $t('change') }}</span
              >
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="table-container show-medium">
      <div v-for="user in info.users" :key="user.id" class="table-line">
        <div class="line column">
          <div class="line-content title">
            {{ $t('fullName') }}
          </div>
          <div class="line-content">
            <div class="item bold">{{ user.firstName }} {{ user.lastName }}</div>
          </div>
        </div>

        <div class="line column">
          <div class="line-content title">
            {{ $t('email') }}
          </div>
          <div class="line-content">
            <div class="item">{{ user.email }}</div>
          </div>
        </div>

        <div class="line column">
          <div class="line-content title">
            {{ $t('type') }}
          </div>
          <div class="line-content">
            <div class="item">{{ user.isMainUser ? $t('mainUser') : $t('secondaryUser') }}</div>
          </div>
        </div>

        <div class="line column">
          <div class="line-content title">
            {{ $t('creation') }}
          </div>
          <div class="line-content">
            <div class="item">{{ $utils.formatDate(user.createdAt) }}</div>
          </div>
        </div>

        <div class="line column">
          <div class="line-content title">
            {{ $t('disable') }}
          </div>
          <div class="line-content">
            <div class="item">{{ !user.isActivated ? $utils.formatDate(user.updatedAt) : '-' }}</div>
          </div>
        </div>

        <div class="line column">
          <div class="line-content">
            <div
              class="item"
              @click="$router.push(`/settings/users/edit?email=${user.email}&firstName=${user.firstName}&lastName=${user.lastName}&userId=${user.id}`)"
            >
              <span class="link-button">{{ $t('change') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    info: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      searchValue: '',
      headers: [this.$t('fullName'), this.$t('email'), this.$t('type'), this.$t('creation'), this.$t('disable'), ''],
    };
  },

  computed: {
    filteredUser() {
      const users = this.info.users;

      if (!this.searchValue) return users;

      return users.filter(x =>
        this.searchValue.split(' ').every(word => {
          const regex = new RegExp(word.toLowerCase());
          return (
            regex.test(x.firstName.toLowerCase()) ||
            regex.test(x.lastName.toLowerCase()) ||
            regex.test(x.email.toLowerCase()) ||
            regex.test(x.isMainUser ? this.$t('mainUser').toLowerCase() : this.$t('secondaryUser').toLowerCase())
          );
        }),
      );
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive.scss';
@use '@/styles/fonts.scss';
.input {
  width: 100%;
  height: 48px;
  padding: 12px 16px;
  box-sizing: border-box;
  color: colors.$text-color;
  border: solid 1px colors.$lightgrey-color;
  border-radius: 4px;
  background: colors.$white-color;

  &::placeholder {
    color: colors.$lightgrey-color;
  }

  &.incorrect {
    border-color: colors.$error-color;
  }
}

.form-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-right: 8px;

  .input-title {
    width: 100%;
    color: colors.$darkgrey-color;
    font-weight: 500;
    font-size: 16px;
    margin: 24px 0 10px 0;
  }

  &.search {
    width: 200px;
  }
}

.table-container {
  .header,
  .control-container {
    margin: 60px 0 0;
    display: flex;
    justify-content: space-between;
    .title {
      display: flex;
      align-items: flex-end;
      font-weight: 500;
      font-size: 20px;
    }
    .main-button {
      max-width: 200px;
      padding: 0;
    }
  }

  .control-container {
    margin-top: 24px;
  }
}

.table .table-content .table-line {
  background: colors.$white-color;

  &.empty .table-item .item {
    height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: colors.$lightgrey-color;
    font-size: 14px;
    font-style: italic;
  }

  &:last-child .table-item {
    border: none;
  }

  .table-item {
    background: transparent;

    &.no-result {
      width: 370%;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .item {
      color: colors.$darkgrey-color;
      font-size: 13px;
      font-weight: normal;
      display: flex;
      justify-content: space-between;
      &.bold {
        font-weight: 500;
      }
    }
  }
}

@media only screen and (max-width: responsive.$layout-breakpoint-medium) {
  .control-container {
    flex-direction: column;

    .search,
    .main-button {
      width: 100%;
      margin-bottom: 10px;
    }
  }

  .header {
    flex-direction: column;
  }

  .table-container {
    width: 100%;

    .table-line {
      width: 100%;
      margin: 12px 0;
      display: flex;
      flex-direction: column;

      background: colors.$unread-table-color;
      padding: 20px;
      box-sizing: border-box;
      border-radius: 4px;

      .line {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-grow: 1;

        margin: 12px 0;

        &.column {
          flex-direction: column;
          justify-content: space-evenly;
          align-items: flex-start;
        }

        .line-content {
          display: flex;
          align-items: center;
          justify-content: space-between;
          width: 100%;

          &.title {
            font-weight: 500;
            color: colors.$grey-color;
            line-height: 16px;
            font-size: 14px;
            margin-bottom: 8px;
          }

          .item {
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
        }
      }
    }
  }
}
</style>
